<template>
  <b-modal
    v-model="isVisible"
    :title="`Reorder: ${categoryName}`"
    hide-header-close
    
    centered
    modal-class="reorder-modal"
    @hidden="onClose"
  >
    <template #modal-header>
      <div class="modal-header-content">
        <h5 class="modal-title">Reorder: {{ categoryName || '[Category Name]' }}</h5>
      </div>
    </template>

    <div class="reorder-modal-body">
      <p class="hierarchy-display">Hierarchy: #{{ currentHierarchy }}</p>
      
      <div class="form-group">
        <label class="form-label">
          Move to<span class="text-danger">*</span>
        </label>
        <input
          v-model="newHierarchy"
          type="number"
          class="form-control"
          :class="{ 'is-invalid': errorMessage }"
          placeholder="Input Hierarchy Number"
          min="1"
          @input="clearError"
          @keyup.enter="handleConfirm"
        />
        <div v-if="errorMessage" class="invalid-feedback d-block">
          {{ errorMessage }}
        </div>
      </div>
    </div>

    <template #modal-footer>
      <button
        type="button"
        class="btn btn-confirm"
        @click="handleConfirm"
      >
        Confirm
      </button>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: 'ReorderModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    categoryName: {
      type: String,
      default: '',
    },
    currentHierarchy: {
      type: [Number, String],
      default: 1,
    },
  },
  data() {
    return {
      newHierarchy: '',
      errorMessage: '',
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set() {
        // no-op here; rely on the modal's hidden event to trigger onClose
        // which will emit the 'close' event to the parent. This avoids
        // duplicate 'close' emits when the modal is hidden by backdrop/esc.
      },
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.newHierarchy = '';
        this.errorMessage = '';
      }
    },
  },
  methods: {
    clearError() {
      this.errorMessage = '';
    },
    handleConfirm() {
      const value = parseInt(this.newHierarchy);
      
      if (!this.newHierarchy || isNaN(value) || value <= 0) {
        this.errorMessage = 'Please enter a valid hierarchy number greater than 0';
        return;
      }

      if (value === parseInt(this.currentHierarchy)) {
        this.errorMessage = 'The hierarchy you entered is the same as the current hierarchy';
        return;
      }
      
      this.$emit('confirm', value);
      this.onClose();
    },
    onClose() {
      this.newHierarchy = '';
      this.errorMessage = '';
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
::v-deep .reorder-modal .modal-dialog {
  max-width: 500px;
}

::v-deep .reorder-modal .modal-content {
  background: #D7C6DF;
  border-radius: 12px;
  border: none;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

::v-deep .reorder-modal .modal-header {
  border-bottom: none;
  padding: 24px 24px 0px;
}

.modal-header-content {
  width: 100%;
}

.modal-title {
  color: #111827;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}

::v-deep .reorder-modal .modal-body {
  padding: 0px 24px 0px;
}

.reorder-modal-body {
  width: 100%;
}

.hierarchy-display {
  color: #111827;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 20px 0;
}

.form-group {
  margin-bottom: 0;
}

.form-label {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
}

.form-control {
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background-color: #F9FAFB;
  color: #111827;
  font-size: 14px;
  transition: all 0.2s ease;
}

.form-control::placeholder {
  color: #9CA3AF;
  opacity: 1;
}

.form-control:focus {
  border-color: #E83092;
  outline: none;
  background-color: #fff;
  box-shadow: 0 0 0 3px rgba(232, 48, 146, 0.1);
}

.form-control.is-invalid {
  border-color: #EF4444;
  background-color: #FEF2F2;
}

.form-control.is-invalid:focus {
  border-color: #EF4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.invalid-feedback {
  color: #DC2626;
  font-size: 13px;
  margin-top: 6px;
  display: block;
}

::v-deep .reorder-modal .modal-footer {
  border-top: none;
  padding: 16px 24px 24px;
  display: flex;
  justify-content: flex-end;
}

.btn-confirm {
  background: #E83092;
  color: #fff;
  border: none;
  padding: 10px;
  border-radius: 4px;
  font-size: 15px;
  font-weight: 600;
  box-shadow: 0 6px 18px rgba(232, 48, 146, 0.18);
  transition: all 0.2s ease;
  cursor: pointer;
}

.btn-confirm:hover {
  background: #d2267e;
  box-shadow: 0 8px 20px rgba(232, 48, 146, 0.25);
  transform: translateY(-1px);
}

.btn-confirm:active {
  transform: translateY(0);
}

.btn-confirm:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(232, 48, 146, 0.2);
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}
</style>
